import axios from 'axios';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';
import api from './itemsApiClient';

const API_BASE_URL = process.env.REACT_APP_ITEMS_API_URL ||
  getMarketplaceApiBaseUrl() || 'http://localhost:8080/api';

export const artistItemsAPI = {
  buildShopRef: (shopOrId) => {
    const id = typeof shopOrId === 'object' ? shopOrId?.id : shopOrId;
    return `ASH-${id}`;
  },

  getArtistItemsPage: async (filters = {}, page = 1, pageSize = 24) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    const response = await axios.get(`${API_BASE_URL}/public/artist-items/?${params.toString()}`);
    const data = response.data;
    if (Array.isArray(data)) {
      return {
        results: data,
        count: data.length,
        page: 1,
        page_size: data.length || pageSize,
        total_pages: 1,
        has_next: false,
      };
    }
    return data;
  },

  getArtistItems: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/public/artist-items/?${query}` : `${API_BASE_URL}/public/artist-items/`;
    const response = await axios.get(url);
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getArtistItemByRef: async (itemRef) => {
    const response = await api.get(`/public/artist-items/${encodeURIComponent(itemRef)}/`);
    return response.data;
  },

  createArtistItemWithImages: async (formData) => {
    const response = await api.post('/artist-items/upload/', formData, { timeout: 120000 });
    return response.data;
  },

  createArtistItemDraft: async (formData) => {
    const response = await api.post('/artist-items/draft/', formData, { timeout: 60000 });
    return response.data;
  },

  uploadArtistItemMedia: async (itemId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/artist-items/${itemId}/images/`, formData, { timeout: 90000 });
    return response.data;
  },

  finalizeArtistItemDraft: async (itemId) => {
    const response = await api.post(`/artist-items/${itemId}/finalize/`, {}, { timeout: 30000 });
    return response.data;
  },

  initArtistDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/artist-items/${itemId}/uploads/init/`, payload, { timeout: 30000 });
    return response.data;
  },

  signArtistDirectParts: async (itemId, payload) => {
    const response = await api.post(`/artist-items/${itemId}/uploads/sign-parts/`, payload, { timeout: 30000 });
    return response.data;
  },

  completeArtistDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/artist-items/${itemId}/uploads/complete/`, payload, { timeout: 60000 });
    return response.data;
  },

  abortArtistDirectUpload: async (itemId, payload) => {
    await api.post(`/artist-items/${itemId}/uploads/abort/`, payload, { timeout: 15000 });
  },

  /** Attach media uploaded at pick time (item-less pending uploads) to a draft. */
  attachArtistPendingImages: async (itemId, keys) => {
    const response = await api.post(
      `/artist-items/${itemId}/images/attach-pending/`,
      { keys },
      { timeout: 60000 },
    );
    return response.data;
  },

  // ── Artist Shops ──────────────────────────────────────────────────────
  getShops: async () => {
    const response = await axios.get(`${API_BASE_URL}/public/artist-shops/`);
    return response.data;
  },

  getShopByRef: async (shopRef) => {
    const response = await axios.get(`${API_BASE_URL}/public/artist-shops/${encodeURIComponent(shopRef)}/`);
    return response.data;
  },

  getShopReviews: async (shopRef) => {
    const response = await axios.get(`${API_BASE_URL}/public/artist-shops/${encodeURIComponent(shopRef)}/reviews/`);
    return response.data;
  },

  addShopReview: async (shopRef, rating, comment) => {
    const response = await api.post(`/public/artist-shops/${encodeURIComponent(shopRef)}/reviews/`, { rating, comment });
    return response.data;
  },

  getMyShop: async ({ compact } = {}) => {
    const response = await api.get('/artist-shops/my/', {
      params: compact ? { compact: '1' } : undefined,
    });
    return response.data;
  },

  createShop: async (formData) => {
    const response = await api.post('/artist-shops/my/', formData);
    return response.data;
  },

  updateShop: async (formData) => {
    const response = await api.put('/artist-shops/my/', formData);
    return response.data;
  },
};
